import { Auth } from 'aws-amplify';
import { KB_AUTOSUGGEST_GRAPHQL_CONFIG } from 'src/constants/AppConstants';

// The appSearch query lives on the same AppSync API as autosuggest, so it
// reuses KB_AUTOSUGGEST_GRAPHQL_CONFIG for endpoint resolution.
const getEndpoint = () => {
  const Stage = 'beta';
  const entry = KB_AUTOSUGGEST_GRAPHQL_CONFIG.find((c) => c.environment === Stage);
  if (!entry || !entry.grapQLEndPoint) {
    throw new Error(`No App Search endpoint configured for stage '${Stage}'`);
  }
  return entry.grapQLEndPoint;
};

const getIdToken = async () => {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
};

/**
 * Query the OpenSearch-backed appSearch API.
 *
 * @param {string} query  Blank/empty -> returns all applications (grid load).
 *                        Non-empty   -> full-text matches, deduped per app.
 * @param {number} size   Max results.
 * @returns {Promise<Array<{appId, appName, description, score}>>}
 */
export const appSearch = async (query = '', size = 500, { signal } = {}) => {
  const endpoint = getEndpoint();
  const token = await getIdToken();

  const gql = `query AppSearch($input: AppSearchInput!) {
    appSearch(input: $input) {
      results {
        appId
        appName
        description
        url
        score
      }
      total
    }
  }`;

  const variables = {
    input: {
      query: query || '',
      ...(size != null && { size }),
    },
  };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ query: gql, variables }),
    ...(signal && { signal }),
  });

  if (!response.ok) {
    throw new Error(`appSearch request failed with status ${response.status}`);
  }

  const result = await response.json();

  if (result?.errors) {
    const err = new Error('appSearch returned GraphQL errors');
    err.graphqlErrors = result.errors;
    throw err;
  }

  return result?.data?.appSearch?.results || [];
};
